/**
 * Capture from your phone. Anything shared to ONE lands here — from Android's Share menu (ONE installed to the
 * home screen) or the iPhone "Send to ONE" Shortcut, both of which open /share?title=…&text=…&url=…
 * You choose: make tasks from it (Smart add), or keep it as a note.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CheckSquare, NotebookPen } from "lucide-react";
import { useMemo, useState } from "react";
import { post } from "../lib/api";
import { SmartAdd } from "../components/QuickAdd";
import { Card, ErrorNote, PageHeader } from "../components/ui";

export function SharePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const shared = useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    const parts = [q.get("title"), q.get("text"), q.get("url")].map((x) => x?.trim()).filter((x): x is string => !!x);
    return [...new Set(parts)].join("\n\n").slice(0, 12000);
  }, []);
  const [text, setText] = useState(shared);
  const [mode, setMode] = useState<"choose" | "tasks">("choose");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const asNote = async () => {
    setBusy(true);
    setError(null);
    try {
      const first = text.split("\n").find((l) => l.trim())?.trim().slice(0, 120) ?? "Shared";
      const body = text.split("\n").map((l) => `<p>${l.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!)}</p>`).join("");
      const n = await post<{ id: string }>("/api/notes", { title: first, body, folder: "Captured", tags: ["phone"] });
      void qc.invalidateQueries({ queryKey: ["notes"] });
      window.location.href = `/notes?note=${n.id}`;
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader title="Add to ONE" subtitle="What you shared — turn it into tasks, or keep it as a note." />
      {mode === "tasks" ? (
        <Card><SmartAdd initial={text} onDone={() => void navigate({ to: "/" })} /></Card>
      ) : (
        <Card>
          <textarea className="input min-h-48 text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="Nothing came through — paste or type here." autoFocus={!shared} />
          <ErrorNote error={error} />
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <button className="btn btn-primary justify-center" disabled={!text.trim() || busy} onClick={() => setMode("tasks")}><CheckSquare size={16} /> Make tasks</button>
            <button className="btn justify-center" disabled={!text.trim() || busy} onClick={() => void asNote()}><NotebookPen size={16} /> Save as note</button>
          </div>
          <p className="muted mt-2 text-xs">Make tasks: Fusion AI picks out what needs doing and you confirm each one. Save as note: kept in Notes → Captured.</p>
        </Card>
      )}
    </div>
  );
}
